import type { InputOf, OutputOf } from './generated/contracts';

export type SellerOrders = OutputOf<'orders.seller.list'>;
export type SellerOrderRow = SellerOrders['orders'][number];
export type SellerOrderStatus = SellerOrderRow['status'];
export type SellerOrderFilter = NonNullable<InputOf<'orders.seller.list'>['filter']>;
export type SellerOrderDetail = OutputOf<'orders.seller.get'>;
export type SellerOrderProduct = SellerOrderDetail['products'][number];
export type BuyerOrders = OutputOf<'orders.order.list'>;
export type BuyerOrderRow = BuyerOrders['orders'][number];
export type BuyerOrderDetail = OutputOf<'orders.order.get'>;
export type BuyerOrderStatus = BuyerOrderRow['status'];

/**
 * **The words a seller reads for an order's status** (GO-280). The server decides which status
 * applies (`domains/orders/order-status.ts`); the clients only name it, and both name it the
 * same way because both read this. The flow is Paid → Ready to ship → Shipped: there is no
 * "To ship" (2026-10-22).
 */
export const SELLER_ORDER_STATUS_LABELS: Record<SellerOrderStatus, string> = {
  awaiting_payment: 'Awaiting payment',
  paid: 'Paid',
  ready_to_ship: 'Ready to ship',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

/**
 * **The words a buyer reads for an order's status** (GO-277, GO-281), as the buyer designs draw
 * them. The server decides which applies (`domains/orders/order-status.ts`).
 */
export const BUYER_ORDER_STATUS_LABELS: Record<BuyerOrderStatus, string> = {
  awaiting_payment: 'Awaiting payment',
  paid: 'Paid',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

/** The order history's filters, in GO-276's order. */
export const SELLER_ORDER_FILTER_LABELS: Record<SellerOrderFilter, string> = {
  awaiting_payment: 'Awaiting payment',
  paid: 'Paid',
  ready_to_ship: 'Ready to ship',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled_refunded: 'Cancelled/Refunded',
  has_issue: 'Has an issue',
};

/** How a product was bought (GO-278). */
export const ORDER_PRODUCT_KIND_LABELS: Record<SellerOrderProduct['kind'], string> = {
  auction_win: 'Auction win',
  bin: 'Buy It Now',
};

/** Each itemized line's name on the detail page. What the seller bears is a deduction (GO-278). */
export const ORDER_FEE_LABELS: Record<SellerOrderProduct['fees'][number]['type'], string> = {
  item: 'Item',
  shipping: 'Shipping',
  tax: 'Tax',
  commission: 'Commission',
  processing: 'Processing',
};

/** Where an order was sold: its show, or a timed auction, which has none (GO-276). */
/** "@thriftwithjo", or nothing for someone with no handle (captured at sale, absent on older orders). */
export const atHandle = (handle: string | undefined): string | undefined => (handle ? `@${handle}` : undefined);

/** "Visa ending 4417": the card a charge used, brand and last four only (GO-279 AC4). */
export const cardLabel = ({ brand, last4 }: { brand: string; last4: string }): string =>
  `${CARD_BRANDS[brand] ?? brand.charAt(0).toUpperCase() + brand.slice(1)} ending ${last4}`;

/** Stripe's brand ids, in the words a card says. An unknown one is capitalized as sent. */
const CARD_BRANDS: Record<string, string> = {
  amex: 'American Express',
  diners: 'Diners Club',
  discover: 'Discover',
  jcb: 'JCB',
  mastercard: 'Mastercard',
  unionpay: 'UnionPay',
  visa: 'Visa',
};

export function orderShowLabel(row: { show?: { title?: string | undefined } | undefined }): string {
  if (!row.show) return 'Timed auction';
  return row.show.title ?? 'Live show';
}

/**
 * **The order history's tabs, as the design draws them** (Order History 1a): one per open status so
 * a tap pulls exactly that list, Cancelled for cancelled and refunded, and Completed for delivered.
 * Under review: Kelli still confirms the list, so the grouping lives here, in one table, rather than
 * in the API. `statuses` is what `orders.seller.list` is asked for. An "Updating card" tab joins
 * only if payment declines go Option B.
 */
export const SELLER_ORDER_TABS = [
  {
    id: 'all',
    label: 'All',
    empty: ['No orders yet', 'When a Buyer wins or buys one of your Products, the order shows up here.'],
  },
  {
    id: 'awaiting_payment',
    label: 'Awaiting payment',
    statuses: ['awaiting_payment'],
    empty: ['Nothing awaiting payment', 'Orders waiting on the Buyer’s payment show up here.'],
  },
  {
    id: 'paid',
    label: 'Paid',
    statuses: ['paid'],
    empty: ['No paid orders', 'Paid orders waiting to be packed show up here.'],
  },
  {
    id: 'ready_to_ship',
    label: 'Ready to ship',
    statuses: ['ready_to_ship'],
    empty: ['Nothing ready to ship', 'Orders with a label, ready to hand off, show up here.'],
  },
  {
    id: 'shipped',
    label: 'Shipped',
    statuses: ['shipped'],
    empty: ['Nothing shipped', 'Orders on their way to the Buyer show up here.'],
  },
  {
    id: 'cancelled',
    label: 'Cancelled',
    statuses: ['cancelled', 'refunded'],
    empty: ['No cancelled orders', 'Cancelled and refunded orders show up here.'],
  },
  {
    id: 'completed',
    label: 'Completed',
    statuses: ['delivered'],
    empty: ['No completed orders yet', 'Delivered orders show up here.'],
  },
] as const satisfies readonly {
  id: string;
  label: string;
  statuses?: readonly SellerOrderStatus[];
  empty: readonly [string, string];
}[];
export type SellerOrderTab = (typeof SELLER_ORDER_TABS)[number];

/**
 * **The buyer's Purchases filters, as the design draws them** (Purchases 1a): All, then one per
 * status. `filter` is what `orders.order.list` is asked for.
 */
export const BUYER_ORDER_FILTERS = [
  {
    id: 'all',
    label: 'All',
    empty: ['No purchases yet', 'When you win an auction or buy a Product, it shows up here.'],
  },
  {
    id: 'awaiting_payment',
    label: 'Awaiting payment',
    filter: 'awaiting_payment',
    empty: ['Nothing awaiting payment', 'Purchases waiting on your payment show up here.'],
  },
  {
    id: 'paid',
    label: 'Paid',
    filter: 'paid',
    empty: ['No paid purchases', 'Paid purchases waiting to ship show up here.'],
  },
  {
    id: 'shipped',
    label: 'Shipped',
    filter: 'shipped',
    empty: ['Nothing shipped', 'Purchases on their way to you show up here.'],
  },
  {
    id: 'delivered',
    label: 'Delivered',
    filter: 'delivered',
    empty: ['Nothing delivered yet', 'Delivered purchases show up here.'],
  },
  {
    id: 'cancelled',
    label: 'Cancelled',
    filter: 'cancelled',
    empty: ['No cancelled purchases', 'Cancelled purchases show up here.'],
  },
  {
    id: 'refunded',
    label: 'Refunded',
    filter: 'refunded',
    empty: ['No refunds', 'Refunded purchases show up here.'],
  },
  // Orders won at auction, whatever their status; Buy It Now stays out (decided 2026-10-23).
  {
    id: 'bids',
    label: 'Bids',
    kind: 'auction_win',
    empty: ['No auction wins yet', 'Products you win at auction show up here.'],
  },
] as const satisfies readonly {
  id: string;
  label: string;
  filter?: BuyerOrderStatus;
  kind?: NonNullable<InputOf<'orders.order.list'>['kind']>;
  empty: readonly [string, string];
}[];
export type BuyerOrderFilter = (typeof BUYER_ORDER_FILTERS)[number];

/** Each timeline step's words (GO-280). */
export const ORDER_TIMELINE_LABELS: Record<SellerOrderDetail['timeline'][number]['step'], string> = {
  sold: 'Sold',
  awaiting_payment: 'Awaiting payment',
  paid: 'Paid',
  label_created: 'Label created',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

/**
 * The same steps in the buyer's words (GO-279 design 1a, 1b): the sale is when their order was
 * created, and a charge in progress is "Processing payment", never something for them to do.
 */
export const BUYER_TIMELINE_LABELS: typeof ORDER_TIMELINE_LABELS = {
  ...ORDER_TIMELINE_LABELS,
  sold: 'Sold · Order created',
  awaiting_payment: 'Processing payment',
};

/** "Levi's 501 jeans + 2 more": what an order is, in one line. */
export const orderProductsLine = ({
  productTitles,
  productCount,
}: {
  productTitles: readonly string[];
  productCount: number;
}) => {
  const [first = 'Order'] = productTitles;
  return productCount > 1 ? `${first} + ${productCount - 1} more` : first;
};

/** "1 Product", "3 Products": the design capitalizes Product, as the Definitions page does. */
export const productCountLabel = (count: number) => `${count} ${count === 1 ? 'Product' : 'Products'}`;

/** An order detail's Products card: "Product" for one (the design's one Product per order), the count for several. */
export const productsTitle = (count: number) => (count === 1 ? 'Product' : `Products · ${count}`);

/** Records show absolute dates with the year (Order History): "Oct 12, 2026". */
export const orderDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** "Oct 12, 2026 · 8:14 PM", for the detail page and the timeline. */
export const orderDateTime = (iso: string) => {
  const date = new Date(iso);
  return `${orderDate(iso)} · ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
};

/** "$142.00", "−$14.20": the detail page's money always shows cents, and a deduction its sign. */
export const orderMoney = (cents: number) =>
  `${cents < 0 ? '−' : ''}${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(cents) / 100)}`;
